"use client";

import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  VibeBlockDesignWizardAnswerType,
  VibeBlockDesignWizardOptionType,
  VibeBlockDesignWizardQuestionType
} from '../api.types';
import { VibeBlockDesignWizardOptionArtwork } from './VibeBlockDesignWizardOptionArtwork';
import '../styles/vibe-block-design-wizard.css';

type VibeBlockDesignWizardCardProps = {
  wizard: VibeBlockDesignWizardQuestionType;
  answer?: VibeBlockDesignWizardAnswerType;
  completedSteps?: VibeBlockDesignWizardCompletedStepType[];
  disabled?: boolean;
  isAdvancing?: boolean;
  language?: 'en' | 'ua' | 'ru' | 'es';
  onSubmit: (answer: VibeBlockDesignWizardAnswerType) => Promise<void> | void;
};

export type VibeBlockDesignWizardCompletedStepType = {
  wizard: VibeBlockDesignWizardQuestionType;
  answer: VibeBlockDesignWizardAnswerType;
};

const WIZARD_COPY = {
  en: {
    name: 'Vibe block design',
    step: (current: number, total: number) => `Step ${current} of ${total}`,
    customLabel: 'Or describe your own idea',
    customPlaceholder: 'For example: keep this structure, but make the cards react to the cursor…',
    answerSaved: 'Answer saved',
    choices: 'Your choices',
    briefReady: 'Design brief ready',
    preparing: 'Preparing the next visual question…',
    skip: 'Continue without questions',
    skipping: 'Continuing without questions…',
    generating: 'Generating your design…',
    sending: 'Sending…',
    continue: 'Continue',
    generate: 'Generate design',
    fallbackExplanation: 'A visual version of this direction',
    recommended: 'Recommended'
  },
  ua: {
    name: 'Дизайн Vibe-блока',
    step: (current: number, total: number) => `Крок ${current} із ${total}`,
    customLabel: 'Або опишіть свій варіант',
    customPlaceholder: 'Наприклад: залишити цю структуру, але щоб картки реагували на рух курсора…',
    answerSaved: 'Відповідь збережено',
    choices: 'Ваш вибір',
    briefReady: 'Дизайн-бриф готовий',
    preparing: 'Готуємо наступне візуальне питання…',
    skip: 'Продовжити без питань',
    skipping: 'Продовжуємо без питань…',
    generating: 'Створюємо ваш дизайн…',
    sending: 'Надсилаємо…',
    continue: 'Далі',
    generate: 'Згенерувати дизайн',
    fallbackExplanation: 'Візуальний варіант цього напрямку',
    recommended: 'Рекомендовано'
  },
  ru: {
    name: 'Дизайн Vibe-блока',
    step: (current: number, total: number) => `Шаг ${current} из ${total}`,
    customLabel: 'Или опишите свой вариант',
    customPlaceholder: 'Например: сохранить эту структуру, но сделать так, чтобы карточки реагировали на движение курсора…',
    answerSaved: 'Ответ сохранён',
    choices: 'Ваш выбор',
    briefReady: 'Дизайн-бриф готов',
    preparing: 'Готовим следующий визуальный вопрос…',
    skip: 'Продолжить без вопросов',
    skipping: 'Продолжаем без вопросов…',
    generating: 'Создаём ваш дизайн…',
    sending: 'Отправляем…',
    continue: 'Далее',
    generate: 'Сгенерировать дизайн',
    fallbackExplanation: 'Визуальный вариант этого направления',
    recommended: 'Рекомендуется'
  },
  es: {
    name: 'Diseño de bloque Vibe',
    step: (current: number, total: number) => `Paso ${current} de ${total}`,
    customLabel: 'O describe tu propia idea',
    customPlaceholder: 'Por ejemplo: conserva esta estructura, pero haz que las tarjetas reaccionen al cursor…',
    answerSaved: 'Respuesta guardada',
    choices: 'Tus elecciones',
    briefReady: 'Brief de diseño listo',
    preparing: 'Preparando la siguiente pregunta visual…',
    skip: 'Continuar sin preguntas',
    skipping: 'Continuando sin preguntas…',
    generating: 'Generando tu diseño…',
    sending: 'Enviando…',
    continue: 'Continuar',
    generate: 'Generar diseño',
    fallbackExplanation: 'Una versión visual de esta dirección',
    recommended: 'Recomendado'
  }
} as const;

const stripOuterParentheses = (value: string): string => {
  const normalized = value.trim();
  if (normalized.startsWith('(') && normalized.endsWith(')')) {
    return normalized.slice(1, -1).trim();
  }
  return normalized;
};

function VibeBlockDesignWizardOptionPreview({
  option,
  optionIndex,
  step
}: {
  option: VibeBlockDesignWizardOptionType;
  optionIndex?: number;
  step?: number;
}) {
  const { preview } = option;
  const layout = preview?.layout || 'custom';
  const motion = preview?.motion || 'none';
  const intensity = preview?.intensity || 'low';
  const accent = preview?.accent || 'content';
  const motif = preview?.motif || (layout === 'canvas' || layout === 'custom' ? 'abstract' : 'content');

  return (
    <div className="vibe-block-design-wizard-preview-shell" aria-hidden="true">
      <div
        className={`vibe-block-design-wizard-preview vibe-block-design-wizard-preview--${layout} vibe-block-design-wizard-preview--motion-${motion} vibe-block-design-wizard-preview--intensity-${intensity} vibe-block-design-wizard-preview--accent-${accent} vibe-block-design-wizard-preview--motif-${motif}`}
      >
        <span className="vibe-block-design-wizard-preview__glow" />
        <VibeBlockDesignWizardOptionArtwork option={option} optionIndex={optionIndex} step={step} />
      </div>
    </div>
  );
}

const getSavedAnswerLabel = (
  wizard: VibeBlockDesignWizardQuestionType,
  answer: VibeBlockDesignWizardAnswerType
): string => {
  const selectedOption = answer.option_id
    ? wizard.options.find((option) => option.id === answer.option_id)
    : undefined;

  if (!selectedOption) {
    return answer.answer_text;
  }

  return `${selectedOption.title} (${stripOuterParentheses(selectedOption.explanation)})`;
};

export default function VibeBlockDesignWizardCard({
  wizard,
  answer,
  completedSteps = [],
  disabled = false,
  isAdvancing = false,
  language = 'en',
  onSubmit
}: VibeBlockDesignWizardCardProps) {
  const effectiveLanguage = useMemo(() => {
    const content = `${wizard.question || ''} ${wizard.options?.[0]?.title || ''}`;
    if (/[іїєґ]/i.test(content)) return 'ua';
    if (/[ыэъё]/i.test(content)) return 'ru';
    if (/[áéíóúñ¿¡]/i.test(content)) return 'es';
    return language in WIZARD_COPY ? language : 'en';
  }, [wizard.question, wizard.options, language]);

  const copy = WIZARD_COPY[effectiveLanguage];
  const options = useMemo(
    () => (Array.isArray(wizard.options) ? wizard.options.slice(0, 3) : []),
    [wizard.options]
  );
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(answer?.option_id || null);
  const [customText, setCustomText] = useState(answer?.custom ? answer.answer_text : '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const submittingRef = useRef(false);
  const optionButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    setSelectedOptionId(answer?.option_id || null);
    setCustomText(answer?.custom ? answer.answer_text : '');
    setIsSubmitting(false);
    setIsSkipping(false);
    submittingRef.current = false;
    optionButtonRefs.current = [];
  }, [wizard.wizard_id, wizard.step, answer]);

  const isAnswered = Boolean(answer);
  const interactionsDisabled = disabled || isAnswered || isSubmitting;
  const normalizedCustomText = customText.trim();
  const selectedOption = selectedOptionId
    ? options.find((option) => option.id === selectedOptionId)
    : undefined;
  const canSubmit = !interactionsDisabled && Boolean(selectedOption || normalizedCustomText);
  const answeredOption = answer?.option_id
    ? options.find((option) => option.id === answer.option_id)
    : undefined;
  const displayedSavedAnswer = answeredOption
    ? `${answeredOption.title} (${stripOuterParentheses(answeredOption.explanation)})`
    : answer?.answer_text;

  const handleSubmit = async () => {
    if (!canSubmit || submittingRef.current) {
      return;
    }

    const isCustom = !selectedOption && Boolean(normalizedCustomText);
    const answerText = isCustom
      ? normalizedCustomText
      : `${selectedOption?.title || ''} (${stripOuterParentheses(selectedOption?.explanation || '')})`;

    const nextAnswer: VibeBlockDesignWizardAnswerType = {
      wizard_id: wizard.wizard_id,
      step: wizard.step,
      ...(selectedOption ? { option_id: selectedOption.id } : {}),
      answer_text: answerText,
      custom: isCustom
    };

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onSubmit(nextAnswer);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (interactionsDisabled || submittingRef.current) {
      return;
    }

    const skipAnswer: VibeBlockDesignWizardAnswerType = {
      wizard_id: wizard.wizard_id,
      step: wizard.step,
      answer_text: copy.skip,
      custom: true,
      skip: true
    };

    submittingRef.current = true;
    setIsSubmitting(true);
    setIsSkipping(true);
    try {
      await onSubmit(skipAnswer);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      setIsSkipping(false);
    }
  };

  const savedSteps = completedSteps
    .filter((step, index, allSteps) => (
      allSteps.findIndex((candidate) => candidate.wizard.step === step.wizard.step) === index
    ))
    .sort((left, right) => left.wizard.step - right.wizard.step);

  const savedAnswers = savedSteps.length > 0 && (
    <div className="vibe-block-design-wizard-progress-summary">
      <div className="vibe-block-design-wizard-progress-summary__label">{copy.choices}</div>
      <ol>
        {savedSteps.map((step) => (
          <li key={`${step.wizard.wizard_id}-${step.wizard.step}`}>
            <span className="vibe-block-design-wizard-progress-summary__number">{step.wizard.step}</span>
            <span className="vibe-block-design-wizard-progress-summary__answer">
              {getSavedAnswerLabel(step.wizard, step.answer)}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );

  if (isAnswered) {
    return (
      <section
        className="vibe-block-design-wizard-card vibe-block-design-wizard-card--answered"
        aria-labelledby={`vibe-block-design-wizard-${wizard.wizard_id}-${wizard.step}`}
      >
        <div className="vibe-block-design-wizard-card__header vibe-block-design-wizard-card__header--answered">
          <div>
            <div className="vibe-block-design-wizard-card__eyebrow">{copy.name} · {copy.step(wizard.step, wizard.total_steps)}</div>
            <h3 id={`vibe-block-design-wizard-${wizard.wizard_id}-${wizard.step}`} className="vibe-block-design-wizard-card__question">
              {wizard.question}
            </h3>
          </div>
          <span className="vibe-block-design-wizard-card__answered-check" aria-hidden="true">✓</span>
        </div>
        {savedAnswers || (
          <div className="vibe-block-design-wizard-card__answered">
            <span className="vibe-block-design-wizard-card__answered-label">{copy.answerSaved}</span>
            <span>{displayedSavedAnswer}</span>
            {answeredOption?.business_effect && (
              <span className="vibe-block-design-wizard-card__answered-effect">{answeredOption.business_effect}</span>
            )}
          </div>
        )}
        <div className="vibe-block-design-wizard-card__ready" role="status" aria-live="polite">
          {isAdvancing ? (wizard.step === wizard.total_steps ? copy.generating : copy.preparing) : copy.briefReady}
        </div>
      </section>
    );
  }

  return (
    <section
      className={`vibe-block-design-wizard-card ${disabled ? 'vibe-block-design-wizard-card--static' : ''}`}
      aria-labelledby={`vibe-block-design-wizard-${wizard.wizard_id}-${wizard.step}`}
    >
      <div className="vibe-block-design-wizard-card__header">
        <div>
          <div className="vibe-block-design-wizard-card__eyebrow">{copy.name} · {copy.step(wizard.step, wizard.total_steps)}</div>
          <h3 id={`vibe-block-design-wizard-${wizard.wizard_id}-${wizard.step}`} className="vibe-block-design-wizard-card__question">
            {wizard.question}
          </h3>
          {wizard.helper_text && <p className="vibe-block-design-wizard-card__helper">{wizard.helper_text}</p>}
        </div>
        <div className="vibe-block-design-wizard-card__step-dots" aria-label={copy.step(wizard.step, wizard.total_steps)}>
          {[1, 2, 3].map((step) => (
            <span
              key={step}
              className={step <= wizard.step ? 'is-active' : ''}
            />
          ))}
        </div>
      </div>

      {savedAnswers}

      <div key={`${wizard.wizard_id}-${wizard.step}`} className="vibe-block-design-wizard-step-content">
        <div className="vibe-block-design-wizard-options" role="radiogroup" aria-label={wizard.question}>
          {options.map((option, optionIndex) => {
            const isSelected = selectedOptionId === option.id;
            const explanation = stripOuterParentheses(option.explanation || '');

            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={interactionsDisabled}
                tabIndex={interactionsDisabled ? -1 : (isSelected || (!selectedOptionId && optionIndex === 0) ? 0 : -1)}
                ref={(node) => {
                  optionButtonRefs.current[optionIndex] = node;
                }}
                className={`vibe-block-design-wizard-option ${isSelected ? 'is-selected' : ''}`}
                onClick={() => {
                  setSelectedOptionId(option.id);
                  setCustomText('');
                }}
                onKeyDown={(event) => {
                  let nextIndex: number | null = null;
                  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                    nextIndex = (optionIndex + 1) % options.length;
                  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                    nextIndex = (optionIndex - 1 + options.length) % options.length;
                  } else if (event.key === 'Home') {
                    nextIndex = 0;
                  } else if (event.key === 'End') {
                    nextIndex = options.length - 1;
                  }

                  if (nextIndex === null) {
                    return;
                  }

                  event.preventDefault();
                  const nextOption = options[nextIndex];
                  if (!nextOption) {
                    return;
                  }
                  setSelectedOptionId(nextOption.id);
                  setCustomText('');
                  optionButtonRefs.current[nextIndex]?.focus();
                }}
              >
                <VibeBlockDesignWizardOptionPreview option={option} optionIndex={optionIndex} step={wizard.step} />
                <span className="vibe-block-design-wizard-option__copy">
                  <span className="vibe-block-design-wizard-option__title">
                    {option.title}
                    {optionIndex === 0 && (
                      <span className="vibe-block-design-wizard-option__badge-recommended">
                        <span className="vibe-block-design-wizard-option__badge-star" aria-hidden="true">★</span>
                        {copy.recommended}
                      </span>
                    )}
                    <span className="vibe-block-design-wizard-option__explanation"> ({explanation || copy.fallbackExplanation})</span>
                  </span>
                  <span className="vibe-block-design-wizard-option__effect">{option.business_effect}</span>
                </span>
                <span className="vibe-block-design-wizard-option__check" aria-hidden="true">✓</span>
              </button>
            );
          })}
        </div>

        {wizard.allow_custom && (
          <div className={`vibe-block-design-wizard-custom ${normalizedCustomText ? 'is-active' : ''}`}>
            <label htmlFor={`vibe-block-design-wizard-custom-${wizard.wizard_id}-${wizard.step}`}>
              {copy.customLabel}
            </label>
            <textarea
              id={`vibe-block-design-wizard-custom-${wizard.wizard_id}-${wizard.step}`}
              value={customText}
              disabled={interactionsDisabled}
              maxLength={500}
              rows={2}
              placeholder={copy.customPlaceholder}
              onChange={(event) => {
                setCustomText(event.target.value);
                setSelectedOptionId(null);
              }}
            />
          </div>
        )}

        <div className="vibe-block-design-wizard-card__footer">
          <button
            type="button"
            className="vibe-block-design-wizard-card__skip"
            disabled={interactionsDisabled}
            onClick={() => {
              handleSkip().catch((error) => console.error('Failed to skip Vibe block design wizard', error));
            }}
          >
            {isSkipping ? copy.skipping : copy.skip}
          </button>
          <button
            type="button"
            className="vibe-block-design-wizard-card__submit"
            disabled={!canSubmit}
            onClick={() => {
              handleSubmit().catch((error) => console.error('Failed to submit Vibe block design wizard answer', error));
            }}
          >
            {isSubmitting && !isSkipping
              ? (wizard.step === wizard.total_steps ? copy.generating : copy.preparing)
              : wizard.step === wizard.total_steps ? copy.generate : copy.continue}
          </button>
        </div>
      </div>
    </section>
  );
}
